import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["category", "query", "results", "selectedCount", "selectedInputs", "status"]
  static values = {
    limit: { type: Number, default: 40 },
    url: String
  }

  connect() {
    this.selectedSourceIds = new Set()
    this.debounceTimer = null
    this.abortController = null
    this.fetchSources()
  }

  disconnect() {
    window.clearTimeout(this.debounceTimer)
    this.abortController?.abort()
  }

  search() {
    window.clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => this.fetchSources(), 250)
  }

  toggle(event) {
    const sourceId = event.currentTarget.value

    if (event.currentTarget.checked) {
      this.selectedSourceIds.add(sourceId)
    } else {
      this.selectedSourceIds.delete(sourceId)
    }

    this.renderSelectedInputs()
    this.updateSelectedCount()
  }

  fetchSources() {
    if (!this.hasUrlValue) return

    this.abortController?.abort()
    this.abortController = new AbortController()
    this.statusTarget.textContent = "Loading sources..."

    window.fetch(this.searchUrl(), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load feed sources")
        return response.json()
      })
      .then((data) => {
        this.renderResults(data.sources || [])
        this.statusTarget.textContent = this.statusText(data.sources || [], data.limit)
      })
      .catch((error) => {
        if (error.name === "AbortError") return

        this.resultsTarget.replaceChildren()
        this.statusTarget.textContent = "Sources could not be loaded"
      })
  }

  searchUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    const query = this.queryTarget.value.trim()
    const category = this.categoryTarget.value.trim()

    if (query) url.searchParams.set("q", query)
    if (category) url.searchParams.set("category", category)
    url.searchParams.set("limit", this.limitValue.toString())

    return url
  }

  renderResults(sources) {
    this.resultsTarget.replaceChildren()

    if (sources.length === 0) {
      const emptyState = document.createElement("div")
      emptyState.className = "nfr-source-picker-empty-row"
      emptyState.textContent = "No matching sources"
      this.resultsTarget.appendChild(emptyState)
      return
    }

    sources.forEach((source) => {
      this.resultsTarget.appendChild(this.resultNode(source))
    })
  }

  resultNode(source) {
    const label = document.createElement("label")
    label.className = "nfr-source-picker-item"

    const checkbox = document.createElement("input")
    checkbox.type = "checkbox"
    checkbox.className = "nfr-form-checkbox nfr-source-picker-check"
    checkbox.value = source.id
    checkbox.checked = this.selectedSourceIds.has(source.id)
    checkbox.dataset.action = "change->feed-source-picker#toggle"

    const content = document.createElement("span")
    content.className = "nfr-source-picker-content"

    const name = document.createElement("span")
    name.className = "nfr-source-picker-name"
    name.textContent = source.name

    const meta = document.createElement("span")
    meta.className = "nfr-source-picker-meta"
    meta.appendChild(this.badge(source.category || "Uncategorized", "nfr-badge-primary"))

    if (source.location) {
      meta.appendChild(this.badge(source.location, "nfr-badge-muted"))
    }

    if (source.coverage_scope) {
      meta.appendChild(this.badge(this.titleize(source.coverage_scope), "nfr-badge-muted"))
    }

    if (source.unhealthy) {
      meta.appendChild(this.badge("Needs review", "nfr-badge-warning"))
    }

    const count = document.createElement("span")
    count.className = "nfr-source-picker-count"
    count.textContent = this.readerCount(source.feed_count)

    content.append(name, meta)
    label.append(checkbox, content, count)

    return label
  }

  badge(text, className) {
    const badge = document.createElement("span")
    badge.className = `nfr-badge ${className}`
    badge.textContent = text
    return badge
  }

  readerCount(value) {
    const count = Number(value || 0)
    return `${count} ${count === 1 ? "reader" : "readers"}`
  }

  titleize(value) {
    return value.toString().replace(/[_-]/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
  }

  renderSelectedInputs() {
    this.selectedInputsTarget.replaceChildren()

    Array.from(this.selectedSourceIds).forEach((sourceId) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "source_feed_ids[]"
      input.value = sourceId
      this.selectedInputsTarget.appendChild(input)
    })
  }

  updateSelectedCount() {
    const count = this.selectedSourceIds.size
    this.selectedCountTarget.textContent = `${count} selected`
  }

  statusText(sources, limit) {
    if (sources.length === 0) return "No sources found"
    if (sources.length >= limit) return `Showing first ${sources.length} matches`

    return `Showing ${sources.length} matches`
  }
}
